import { ReactNode, useCallback } from "react";

import { defineMessage, FormattedMessage, useIntl } from "react-intl";

import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import ReusableCrossflowHeroComponent from "./reusable-xflow-hero-component/reusable-xflow-hero-templates";

type ConfluenceCrossflowCTAProps = {
    contentComponent: React.ComponentType<Record<string, never>>;
    gwp10965XflowRecommendation?: boolean;
};

const CONFLUENCE_UNDERLINE_SVG = (
    <svg
        width="100%"
        height="30"
        viewBox="0 0 306 42"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
            fill="#FFA900"
        />
    </svg>
);

const ConfluenceTextFormatter = ({ children }: { children: ReactNode }) => (
    <Adg4TextUnderline underline={CONFLUENCE_UNDERLINE_SVG}>
        {children}
    </Adg4TextUnderline>
);

const ConfluenceHeroVariantTitle = () => {
    const confluenceFormatter = useCallback(
        (str: ReactNode) => (
            <ConfluenceTextFormatter>{str}</ConfluenceTextFormatter>
        ),
        []
    );

    return (
        <FormattedMessage
            id="HomePageTemplateV2.confluenceCrossflowHeroVariant"
            defaultMessage="Unleash knowledge with{br}<confluence>Confluence</confluence>"
            values={{
                br: <br />,
                confluence: confluenceFormatter
            }}
        />
    );
};

export const ConfluenceCrossflowCTA = ({
    contentComponent,
    gwp10965XflowRecommendation
}: ConfluenceCrossflowCTAProps) => {
    const signupLink = useLocalizedHref("/try/cloud/signup?bundle=confluence");
    const intl = useIntl();
    const confluenceFormatter = useCallback(
        (str: ReactNode) => (
            <ConfluenceTextFormatter>{str}</ConfluenceTextFormatter>
        ),
        []
    );

    const heroTitle = gwp10965XflowRecommendation ? (
        <ConfluenceHeroVariantTitle />
    ) : (
        <FormattedMessage
            id="HomePageTemplateV2.confluenceCrossflowHero"
            defaultMessage="Unleash knowledge{br}with Jira + <confluence>Confluence</confluence>"
            values={{
                confluence: confluenceFormatter,
                br: <br />
            }}
        />
    );

    const getItFreeText = defineMessage({
        id: "HomePageTemplateV2.confluenceCrossflowHero.getStartedCtaButton",
        defaultMessage: "Get started"
    });

    const formattedGetItFreeText = intl.formatMessage(getItFreeText);

    return (
        <ReusableCrossflowHeroComponent
            heroTitle={heroTitle}
            contentComponent={contentComponent}
            ctaButton={{
                buttonText: formattedGetItFreeText,
                buttonHref: signupLink,
                buttonColor: "blue",
                buttonVariant: "primary",
                eventProps: {
                    label: formattedGetItFreeText,
                    labelEnglish: getItFreeText.defaultMessage
                }
            }}
            segmentName="confluence-crossflow-hero"
            sectionId="confluence-crossflow-hero"
            scrollTrackingSection="confluence-crossflow-hero"
            gwp10965XflowRecommendation={gwp10965XflowRecommendation}
        />
    );
};
